import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import { Table, Tooltip } from 'antd'
import {
  MobileCard,
  MobileCardHeader,
  MobileCardLabel,
  MobileCardRow,
  MobileCardStage,
  MobileCardTitle,
  MobileCardValue,
  MobileTableFooter,
  TableBody,
  TableCheckboxWrapper,
  TableContainer,
  TableScroller,
  TotalsTableWrapper,
} from './styles'
import TableFooter from '../../layouts/b2b/table-footer'
import { DataTableProps } from './interface'
import InputNumber from '../input-number'
import InputText from '../input-text'
import InputDate from '../input-date'
import InputPhone from '../input-phone'
import InputCheckbox from '../Input-checkbox'
import InputTime from '../input-time'
import SelectFromEnum from '../select-from-enum'
import { useMediaQuery } from 'react-responsive'
import { StageContainer } from '../../pages_v2/mesurements/list/columns'
import ResizableTitle from './ResizableTitle'
import { useColumnSettings } from './useColumnSettings'

const debounce = (func: (...args: any[]) => void, wait: number) => {
  let timeout: ReturnType<typeof setTimeout>
  return (...args: any[]) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

const EllipsisCell: React.FC<{ value: any; maxLines?: number }> = React.memo(
  ({ value, maxLines }) => {
    const cellRef = useRef<HTMLDivElement>(null)
    const [showTooltip, setShowTooltip] = useState(false)

    useEffect(() => {
      const checkOverflow = () => {
        if (cellRef.current) {
          const { scrollHeight, clientHeight } = cellRef.current
          setShowTooltip(scrollHeight > clientHeight)
        }
      }
      checkOverflow()
    }, [value])

    const content = (
      <div
        ref={cellRef}
        className="cell-content"
        style={maxLines ? { WebkitLineClamp: maxLines } : {}}
      >
        {value}
      </div>
    )

    return showTooltip && maxLines ? (
      <Tooltip title={value} placement="topLeft">
        {content}
      </Tooltip>
    ) : (
      content
    )
  },
  (prevProps, nextProps) =>
    prevProps.value === nextProps.value && prevProps.maxLines === nextProps.maxLines,
)

EllipsisCell.displayName = 'EllipsisCell'

function DataTable<T extends { id: React.Key; type?: string | null } & Record<string, any>>({
  columns,
  mobileFields,
  data,
  loading = false,
  rowKey,
  pagination,
  selectedRowKeys,
  onSelectChange,
  onRowClick,
  onCreate,
  onDelete,
  onPageChange,
  showDelete = false,
  size = 'small',
  className,
  rowClassName,
  bordered = false,
  onTableChange,
  highlightLastRowPrefix = '',
  headerRef,
  editableFields = [],
  onSaveEdit,
  maxLines,
  extraFooterInfo,
  totals,
}: DataTableProps<T>) {
  const internalHeaderRef = useRef<HTMLDivElement | null>(null)
  const effectiveHeaderRef = headerRef || internalHeaderRef
  const [offsetHeader, setOffsetHeader] = useState(0)
  const [lastSelectedOrder, setLastSelectedOrder] = useState<number | null>(null)
  const [editingRows, setEditingRows] = useState<React.Key[]>([])
  const [editedData, setEditedData] = useState<Record<string | number, Partial<T>>>({})
  const editedDataRef = useRef<Record<string | number, Partial<T>>>({})
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const { columnWidths, updateColumnWidth } = useColumnSettings()

  const isEditing = editingRows.length > 0

  const handleEditClick = useCallback(() => {
    setEditingRows(selectedRowKeys || [])
    setEditedData({})
    editedDataRef.current = {}
  }, [selectedRowKeys])
  const handleCancelEdit = useCallback(() => {
    setEditingRows([])
    setEditedData({})
  }, [])

  const handleSaveEdit = useCallback(async () => {
    if (onSaveEdit) {
      const editedRows = editingRows
        .map(rowId => {
          const changes = editedDataRef.current[String(rowId)]
          if (!changes || Object.keys(changes).length === 0) {
            return null
          }
          return {
            id: Number(rowId),
            ...changes,
          }
        })
        .filter(row => row !== null)
      if (editedRows.length > 0) {
        await onSaveEdit(editedRows as T[])
      }
    }
    setEditingRows([])
    setEditedData({})
    editedDataRef.current = {}
  }, [editingRows, onSaveEdit])

  const handleFieldChange = useCallback((rowId: React.Key, field: string, value: any) => {
    editedDataRef.current = {
      ...editedDataRef.current,
      [String(rowId)]: {
        ...(editedDataRef.current[String(rowId)] || {}),
        [field]: value,
      },
    }
    setEditedData(prev => ({
      ...prev,
      [String(rowId)]: {
        ...(prev[String(rowId)] || {}),
        [field]: value,
      },
    }))
  }, [])

  const getColumnRender = useCallback(
    (column: any) => (value: any, record: T) => {
      const isRowEditing = editingRows.includes(String(record.id))
      const isEditable = editableFields.includes(column.dataIndex)

      if (isRowEditing && isEditable && column.input) {
        const { type, format, options, style, view, enum: enumValues, oneOf, key } = column.input
        const UISchema = { options, style, view, enum: enumValues, oneOf }
        const fieldCode = key || column.dataIndex

        switch (type) {
          case 'string':
            if (format === 'date') {
              return (
                <InputDate
                  value={editedData[String(record.id)]?.[fieldCode] || value}
                  onChange={val => handleFieldChange(record.id, fieldCode, val)}
                  UISchema={{ ...UISchema, style: { minWidth: 120 } }}
                />
              )
            }
            if (format === 'time') {
              return (
                <InputTime
                  value={editedData[String(record.id)]?.[fieldCode] || value}
                  onChange={val => handleFieldChange(record.id, fieldCode, val)}
                  UISchema={{ ...UISchema, style: { minWidth: 80 } }}
                />
              )
            }
            if (format === 'phone-client') {
              return (
                <InputPhone
                  value={editedData[String(record.id)]?.[fieldCode] || value}
                  onChange={val => handleFieldChange(record.id, fieldCode, val)}
                  UISchema={UISchema}
                />
              )
            }
            if (column.input.view?.name === 'select-from-enum') {
              return (
                <SelectFromEnum
                  value={editedData[String(record.id)]?.[fieldCode] || value}
                  schema={UISchema}
                  onChange={val => handleFieldChange(record.id, fieldCode, val)}
                  UISchema={{ ...UISchema, style: { minWidth: 160 } }}
                />
              )
            }
            return (
              <InputText
                value={editedData[String(record.id)]?.[fieldCode] || value}
                onChange={val => handleFieldChange(record.id, fieldCode, val)}
                schema={{}}
                UISchema={{ ...UISchema, style: { minWidth: 100 } }}
              />
            )
          case 'number':
            return (
              <InputNumber
                value={
                  editedData[String(record.id)]?.[fieldCode] !== undefined
                    ? editedData[String(record.id)][fieldCode]
                    : value
                }
                onChange={val => handleFieldChange(record.id, fieldCode, val)}
                UISchema={{ ...UISchema, style: { minWidth: 80 } }}
              />
            )
          case 'boolean':
            return (
              <InputCheckbox
                value={
                  editedData[String(record.id)]?.[fieldCode] !== undefined
                    ? editedData[String(record.id)][fieldCode]
                    : value
                }
                onChange={val => {
                  handleFieldChange(record.id, fieldCode, val)
                }}
                UISchema={{
                  ...UISchema,
                  options: { label: ' ', hide_title: true, highlight: true },
                }}
              />
            )
          default:
            return (
              <InputText
                value={editedData[String(record.id)]?.[fieldCode] || value}
                onChange={val => handleFieldChange(record.id, fieldCode, val)}
                schema={{}}
                UISchema={UISchema}
              />
            )
        }
      }

      const displayValue = column.render ? column.render(value, record) : value
      return <EllipsisCell value={displayValue} maxLines={maxLines} />
    },
    [editingRows, editedData, editableFields, handleFieldChange, maxLines],
  )

  const syncColumnWidth = useCallback(
    (columnKey: string, width: number) => {
      if (totalsTableRef.current) {
        const totalsTable = totalsTableRef.current.querySelector('.ant-table')
        if (totalsTable) {
          const columnIndex = columns.findIndex(
            col => String(col.dataIndex || col.key) === columnKey,
          )
          if (columnIndex >= 0) {
            const thElements = totalsTable.querySelectorAll('thead th')
            const tdElements = totalsTable.querySelectorAll('tbody td')
            const targetTh = thElements[columnIndex] as HTMLElement
            const targetTd = tdElements[columnIndex] as HTMLElement

            if (targetTh) {
              targetTh.style.width = `${width}px`
            }
            if (targetTd) {
              targetTd.style.width = `${width}px`
            }
          }
        }
      }
    },
    [columns],
  )

  const hasCustomWidths = columnWidths ? Object.keys(columnWidths).length > 0 : false

  const enhancedColumns = useMemo(
    () =>
      columns.map(column => {
        const columnKey = String(column.dataIndex || column.key)
        const savedWidth = columnWidths?.[columnKey]
        const effectiveWidth = savedWidth || column.width

        return {
          ...column,
          width: effectiveWidth,
          onHeaderCell: (col: any) => ({
            width: effectiveWidth || col.width,
            onResize: (width: number) => {
              syncColumnWidth(columnKey, width)
            },
            onResizeEnd: (width: number) => {
              updateColumnWidth(columnKey, width)
            },
          }),
          render: column.editable
            ? getColumnRender(column)
            : (value: any, record: T, index: number) => {
                const displayValue = column.render ? column.render(value, record, index) : value
                return <EllipsisCell value={displayValue} maxLines={maxLines} />
              },
        }
      }),
    [columns, getColumnRender, maxLines, columnWidths, updateColumnWidth, syncColumnWidth],
  )

  useEffect(() => {
    const updateOffset = debounce(() => {
      if (effectiveHeaderRef.current) {
        setOffsetHeader(effectiveHeaderRef.current.clientHeight + 83)
      }
    }, 100)

    const resizeObserver = new ResizeObserver(updateOffset)
    const mutationObserver = new MutationObserver(updateOffset)

    if (effectiveHeaderRef.current) {
      resizeObserver.observe(effectiveHeaderRef.current)
      mutationObserver.observe(effectiveHeaderRef.current, { childList: true, subtree: true })
    }

    updateOffset()
    window.addEventListener('resize', updateOffset)

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      window.removeEventListener('resize', updateOffset)
    }
  }, [effectiveHeaderRef])

  useEffect(() => {
    const storedOrderId = sessionStorage.getItem(`lastSelectedOrder-${highlightLastRowPrefix}`)
    if (storedOrderId) {
      setLastSelectedOrder(Number(storedOrderId))
      sessionStorage.setItem(`lastSelectedOrder-${highlightLastRowPrefix}`, '')
    }
  }, [highlightLastRowPrefix])

  useEffect(() => {
    if (lastSelectedOrder && data.length > 0) {
      const highlightedRow = document.querySelector(
        '.highlighted-row',
      ) as HTMLTableRowElement | null
      if (highlightedRow) {
        highlightedRow.classList.add('stop-animation')
        highlightedRow.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [lastSelectedOrder, data])

  const tableBodyRef = useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const totalsTableRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const tableBody = document.querySelector('.ant-table-body') as HTMLDivElement | null
    if (tableBody) tableBodyRef.current = tableBody
  }, [data, columns])

  const updateScrollVisibility = useMemo(
    () =>
      debounce(() => {
        if (scrollContainerRef.current && tableBodyRef.current) {
          const scrollContainerWidth = scrollContainerRef.current.clientWidth
          const tableWidth = tableBodyRef.current.scrollWidth
          const shouldHideScroll = scrollContainerWidth + 1 >= tableWidth
          scrollContainerRef.current.style.setProperty(
            '--scroll-display',
            shouldHideScroll ? 'none' : 'block',
          )
        }
      }, 100),
    [],
  )

  useEffect(() => {
    updateScrollVisibility()
    window.addEventListener('resize', updateScrollVisibility)
    const resizeObserver = new ResizeObserver(updateScrollVisibility)
    if (scrollContainerRef.current) resizeObserver.observe(scrollContainerRef.current)

    return () => {
      window.removeEventListener('resize', updateScrollVisibility)
      resizeObserver.disconnect()
    }
  }, [updateScrollVisibility])

  const syncScroll = useCallback(() => {
    if (tableBodyRef.current && scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = tableBodyRef.current.scrollLeft
    }
    if (tableBodyRef.current && totalsTableRef.current) {
      const totalsTableBody = totalsTableRef.current.querySelector(
        '.ant-table-content',
      ) as HTMLDivElement
      if (totalsTableBody) totalsTableBody.scrollLeft = tableBodyRef.current.scrollLeft
    }
  }, [])

  const syncTableScroll = useCallback(() => {
    if (tableBodyRef.current && scrollContainerRef.current) {
      tableBodyRef.current.scrollLeft = scrollContainerRef.current.scrollLeft
    }
    if (totalsTableRef.current && scrollContainerRef.current) {
      const totalsTableBody = totalsTableRef.current.querySelector(
        '.ant-table-content',
      ) as HTMLDivElement
      if (totalsTableBody) totalsTableBody.scrollLeft = scrollContainerRef.current.scrollLeft
    }
  }, [])

  useEffect(() => {
    if (tableBodyRef.current) tableBodyRef.current.addEventListener('scroll', syncScroll)
    if (scrollContainerRef.current)
      scrollContainerRef.current.addEventListener('scroll', syncTableScroll)

    return () => {
      if (tableBodyRef.current) tableBodyRef.current.removeEventListener('scroll', syncScroll)
      if (scrollContainerRef.current)
        scrollContainerRef.current.removeEventListener('scroll', syncTableScroll)
    }
  }, [syncScroll, syncTableScroll])

  const handleCheckboxClick = useCallback(
    (record: T, selected: boolean, e: React.MouseEvent) => {
      e.stopPropagation()
      if (onSelectChange && selectedRowKeys) {
        const newSelectedRowKeys = selected
          ? selectedRowKeys.filter(key => String(key) !== String(record.id))
          : [...selectedRowKeys, String(record.id)]
        onSelectChange(newSelectedRowKeys)
      }
    },
    [onSelectChange, selectedRowKeys],
  )

  const renderTotalsTable = useCallback(() => {
    if (!totals) return null

    const totalsData = [
      columns.reduce((acc, column, index) => {
        if (index === 0) {
          return { ...acc, [String(column.dataIndex)]: 'Всего' }
        }
        return {
          ...acc,
          [String(column.dataIndex)]:
            typeof column.dataIndex === 'string' ? totals[column.dataIndex] ?? '' : '',
        }
      }, {} as Record<string, string | number>),
    ] as T[]

    return (
      <TotalsTableWrapper ref={totalsTableRef}>
        <Table
          columns={enhancedColumns}
          dataSource={totalsData}
          pagination={false}
          showHeader={false}
          scroll={{ x: 'max-content' }}
          size={size}
          bordered={bordered}
          rowClassName={() => 'totals-row'}
          components={{
            header: {
              cell: ResizableTitle,
            },
            body: {
              row: props => <tr {...props} style={{ background: '#fafafa', fontWeight: 'bold' }} />,
            },
          }}
          rowSelection={
            selectedRowKeys && onSelectChange
              ? {
                  renderCell: (value, record, index, originNode) => <div></div>,
                }
              : undefined
          }
        />
      </TotalsTableWrapper>
    )
  }, [totals, data, enhancedColumns, size, bordered, selectedRowKeys, onSelectChange])

  const MemoizedTableFooter = useMemo(
    () =>
      React.memo(
        TableFooter,
        (prevProps, nextProps) =>
          prevProps.pagination === nextProps.pagination &&
          prevProps.selectedRowKeys === nextProps.selectedRowKeys &&
          prevProps.isEditing === nextProps.isEditing &&
          prevProps.extraInfo === nextProps.extraInfo,
      ),
    [],
  )

  const renderMobileView = useCallback(
    (record: T) => {
      if (!mobileFields) return null

      const firstField = mobileFields[0]
      const hasCustomTitle = firstField && firstField.label === ''
      const titleContent = hasCustomTitle
        ? firstField.render
          ? firstField.render(record[firstField.key], record)
          : record[firstField.key]
        : record.production_number

      const fieldsToRender = hasCustomTitle ? mobileFields.slice(1) : mobileFields

      const isStageObject =
        record.stage && typeof record.stage === 'object' && 'name' in record.stage

      const rowClass = rowClassName ? rowClassName(record) : ''
      const highlightedClass = lastSelectedOrder === record.id ? 'highlighted-row' : ''
      const combinedClassName = [rowClass, highlightedClass].filter(Boolean).join(' ')

      return (
        <MobileCard
          onClick={() => !isEditing && onRowClick?.(record)}
          className={combinedClassName}
        >
          <MobileCardHeader>
            {isStageObject && (
              <MobileCardStage>
                <StageContainer stage={record.stage} />
              </MobileCardStage>
            )}
            <MobileCardTitle>{titleContent}</MobileCardTitle>
          </MobileCardHeader>
          {fieldsToRender.map(field => (
            <MobileCardRow key={field.key}>
              <MobileCardLabel>{field.label}:</MobileCardLabel>
              <MobileCardValue>
                {field.render ? field.render(record[field.key], record) : record[field.key]}
              </MobileCardValue>
            </MobileCardRow>
          ))}
        </MobileCard>
      )
    },
    [mobileFields, onRowClick, isEditing, lastSelectedOrder, rowClassName],
  )

  if (isMobile && mobileFields) {
    return (
      <TableContainer className={className} $hasCustomWidths={hasCustomWidths}>
        <TableBody>
          {data.map(record => (
            <React.Fragment key={record.id}>{renderMobileView(record)}</React.Fragment>
          ))}
        </TableBody>
        <MobileTableFooter>
          <MemoizedTableFooter
            pagination={pagination}
            handlePageChange={onPageChange}
            showCreate={onCreate && !isEditing}
            showDelete={showDelete && !isEditing}
            onCreate={onCreate}
            onDelete={onDelete}
            selectedRowKeys={selectedRowKeys || undefined}
            onEdit={selectedRowKeys ? handleEditClick : undefined}
            onSave={handleSaveEdit}
            onCancel={handleCancelEdit}
            isEditing={isEditing}
            extraInfo={extraFooterInfo}
          />
        </MobileTableFooter>
      </TableContainer>
    )
  }

  return (
    <TableContainer className={className} $hasCustomWidths={hasCustomWidths}>
      <Table<T>
        sticky={{ offsetHeader }}
        showHeader={data.length > 0}
        showSorterTooltip={{ overlay: 'Нажмите для сортировки', placement: 'bottom' }}
        columns={enhancedColumns}
        rowKey={rowKey}
        dataSource={data}
        pagination={false}
        loading={loading}
        onChange={onTableChange}
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
        onRow={
          onRowClick
            ? record => ({
                onClick: () => {
                  if (highlightLastRowPrefix && !isEditing) {
                    sessionStorage.setItem(
                      `lastSelectedOrder-${highlightLastRowPrefix}`,
                      String(record?.id),
                    )
                    setLastSelectedOrder(Number(record?.id))
                  }
                  if (!isEditing) {
                    onRowClick(record)
                  }
                },
              })
            : undefined
        }
        scroll={{ x: 'max-content' }}
        size={size}
        rowSelection={
          selectedRowKeys && onSelectChange
            ? {
                selectedRowKeys,
                onChange: onSelectChange,
                getCheckboxProps: () => ({
                  disabled: isEditing,
                }),
                renderCell: (value, record, index, originNode) => (
                  <TableCheckboxWrapper
                    onClick={e =>
                      handleCheckboxClick(record, selectedRowKeys.includes(String(record.id)), e)
                    }
                  >
                    {originNode}
                  </TableCheckboxWrapper>
                ),
              }
            : undefined
        }
        rowClassName={record => {
          const highlightClass =
            highlightLastRowPrefix && lastSelectedOrder !== null && record.id === lastSelectedOrder
              ? 'highlighted-row'
              : ''
          const customClass = rowClassName ? rowClassName(record) : ''
          return [highlightClass, customClass].filter(Boolean).join(' ')
        }}
        bordered={bordered}
        className={!isMobile ? 'sticky-footer' : undefined}
        footer={() => (
          <>
            {data.length && !isEditing ? renderTotalsTable() : ''}
            {!isMobile && (
              <TableScroller
                ref={scrollContainerRef}
                $scrollWidth={tableBodyRef.current?.scrollWidth || undefined}
                style={{ '--scroll-display': 'block' } as React.CSSProperties}
              />
            )}
            <MemoizedTableFooter
              pagination={pagination}
              handlePageChange={onPageChange}
              showCreate={onCreate && !isEditing}
              showDelete={showDelete && !isEditing}
              onCreate={onCreate}
              onDelete={onDelete}
              selectedRowKeys={selectedRowKeys || undefined}
              onEdit={selectedRowKeys ? handleEditClick : undefined}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
              isEditing={isEditing}
              extraInfo={extraFooterInfo}
            />
          </>
        )}
      />
    </TableContainer>
  )
}

export default React.memo(DataTable)
